import { Button } from "@/components/ui/button";
import {
  X, Lightning, Package, UsersThree, Broadcast, Bell, Clock, ArrowRight,
  CheckCircle, ListChecks, Cpu, ShieldCheck,
} from "@phosphor-icons/react";

/**
 * Lightweight "How alerts flow" help drawer.
 * Pure explanation layer — no business logic.
 * Triggered from Event Orchestration header AND Alert Center header.
 */
export default function AlertFlowHelp({ open, onClose }) {
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[1950] bg-black/75 backdrop-blur-sm flex items-stretch justify-center"
      onClick={onClose}
      data-testid="alert-flow-help-overlay"
    >
      <div
        className="bg-zinc-950 border-l border-zinc-800 w-full max-w-2xl ml-auto flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="alert-flow-help-drawer"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold text-zinc-100 flex items-center gap-2">
              <Cpu size={16} weight="duotone" className="text-teal-400" />
              How alerts flow
            </h2>
            <p className="text-[11px] text-zinc-500 mt-0.5">
              The operational pipeline from raw event → operator action → audit trail.
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0" data-testid="alert-flow-help-close">
            <X size={16} />
          </Button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          {/* Two-role mental model */}
          <section data-testid="flow-help-roles">
            <h3 className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Mental model</h3>
            <div className="grid grid-cols-2 gap-2">
              <div className="border border-teal-700/40 bg-teal-500/5 rounded p-3">
                <div className="flex items-center gap-1.5 mb-1">
                  <Cpu size={12} weight="duotone" className="text-teal-300" />
                  <span className="text-[10px] uppercase tracking-wider font-bold text-teal-300">
                    Event Orchestration
                  </span>
                </div>
                <div className="text-[11px] text-zinc-300 leading-snug">
                  <span className="text-teal-300 font-semibold">The brain.</span> Rules engine that
                  decides who gets notified, on which channel, with what SLA & escalation path.
                </div>
                <ul className="mt-2 space-y-0.5 text-[10px] text-zinc-400 list-disc list-inside">
                  <li>Creates alerts (via <code className="text-teal-400 font-mono">emit_event()</code>)</li>
                  <li>Resolves shipment roles → contacts</li>
                  <li>Dispatches channels (in-app / email / SMS / Slack)</li>
                  <li>Manages SLA timers & auto-escalation</li>
                </ul>
              </div>

              <div className="border border-amber-700/40 bg-amber-500/5 rounded p-3">
                <div className="flex items-center gap-1.5 mb-1">
                  <ListChecks size={12} weight="duotone" className="text-amber-300" />
                  <span className="text-[10px] uppercase tracking-wider font-bold text-amber-300">
                    Alert Center
                  </span>
                </div>
                <div className="text-[11px] text-zinc-300 leading-snug">
                  <span className="text-amber-300 font-semibold">The cockpit.</span> Operator work
                  queue. Read-side surface — never sends notifications itself.
                </div>
                <ul className="mt-2 space-y-0.5 text-[10px] text-zinc-400 list-disc list-inside">
                  <li>Lists alerts produced by Orchestration</li>
                  <li>Acknowledge / Resolve / Dismiss</li>
                  <li>Manual Escalate</li>
                  <li>Per-alert timeline + dispatch trail</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Pipeline diagram */}
          <section data-testid="flow-help-pipeline">
            <h3 className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Pipeline</h3>
            <ol className="space-y-2">
              {PIPELINE_STEPS.map((step, i) => (
                <li
                  key={step.label}
                  className="flex items-start gap-3 border border-zinc-800 bg-zinc-900/40 rounded px-3 py-2"
                  data-testid={`flow-help-step-${i}`}
                >
                  <div className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold font-mono ${step.numTone}`}>
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <step.icon size={11} weight="duotone" className={step.tone} />
                      <span className={`text-[11px] font-semibold ${step.tone}`}>{step.label}</span>
                      {step.owner && (
                        <span className={`text-[8px] uppercase tracking-wider font-bold px-1 py-0.5 rounded ${step.ownerTone}`}>
                          {step.owner}
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-zinc-400 leading-snug">{step.desc}</div>
                  </div>
                </li>
              ))}
            </ol>
          </section>

          {/* Hard rule */}
          <section data-testid="flow-help-guardrail">
            <h3 className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Architectural guardrail</h3>
            <div className="border border-zinc-800 bg-zinc-900/40 rounded p-3 flex items-start gap-2">
              <ShieldCheck size={14} weight="duotone" className="text-emerald-400 mt-0.5" />
              <div className="text-[11px] text-zinc-300 leading-snug">
                No module is allowed to call <code className="text-teal-400 font-mono">db.alerts.insert</code>{" "}
                directly. Telemetry ingestion, shipment lifecycle, corrective actions, AI anomalies — all
                must route through <code className="text-teal-400 font-mono">emit_event()</code>. A pytest
                contract enforces this so the rules engine, escalation timers, and dispatch logging are
                never bypassed.
              </div>
            </div>
          </section>

          {/* Channel reality */}
          <section data-testid="flow-help-channels">
            <h3 className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Channel reality (today)</h3>
            <div className="grid grid-cols-2 gap-2 text-[11px]">
              <div className="border border-emerald-700/40 bg-emerald-500/5 rounded p-2.5">
                <div className="text-[10px] uppercase font-bold text-emerald-300 mb-1">In-app · live</div>
                <div className="text-zinc-400">
                  Written to <code className="text-teal-400 font-mono">notification_log</code> with{" "}
                  <code className="text-emerald-300 font-mono">delivered</code>. Surfaces immediately in
                  Alert Center.
                </div>
              </div>
              <div className="border border-amber-700/40 bg-amber-500/5 rounded p-2.5">
                <div className="text-[10px] uppercase font-bold text-amber-300 mb-1">Email / SMS / Slack</div>
                <div className="text-zinc-400">
                  Logged with{" "}
                  <code className="text-amber-300 font-mono">mock_dispatched</code> — no real provider wired
                  yet. Wiring Resend / Twilio / Slack makes them live without changing any orchestration logic.
                </div>
              </div>
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-zinc-800 flex items-center justify-between bg-zinc-950">
          <p className="text-[10px] text-zinc-500">
            Source files · <span className="font-mono text-zinc-400">notifications_routing.py</span>
            {" → "}
            <span className="font-mono text-zinc-400">alerts.py</span>
          </p>
          <Button size="sm" onClick={onClose} className="h-7 text-xs bg-zinc-800 hover:bg-zinc-700 text-zinc-200"
            data-testid="alert-flow-help-got-it">
            <CheckCircle size={11} className="mr-1" weight="duotone" /> Got it
          </Button>
        </div>
      </div>
    </div>
  );
}

const PIPELINE_STEPS = [
  {
    label: "Operational Event",
    desc: "Telemetry breach, shipment lifecycle change, CA overdue, AI anomaly, or manual operator action.",
    icon: Lightning,
    tone: "text-amber-300",
    numTone: "bg-amber-500/15 text-amber-300",
    owner: "Source",
    ownerTone: "bg-zinc-800 text-zinc-400",
  },
  {
    label: "emit_event()",
    desc: "Single sanctioned entry point. Stamps a canonical alert with source + correlation_id (for idempotency).",
    icon: Cpu,
    tone: "text-teal-300",
    numTone: "bg-teal-500/15 text-teal-300",
    owner: "Orchestration",
    ownerTone: "bg-teal-500/15 text-teal-300",
  },
  {
    label: "Event Orchestration Rule",
    desc: "Matches event_type + severity ≥ floor. Picks the shipment roles to notify (e.g. QA, carrier, compliance).",
    icon: Broadcast,
    tone: "text-violet-300",
    numTone: "bg-violet-500/15 text-violet-300",
    owner: "Orchestration",
    ownerTone: "bg-teal-500/15 text-teal-300",
  },
  {
    label: "Shipment Context",
    desc: "Looks up the affected shipment's assigned Operational Parties so roles resolve to real people.",
    icon: Package,
    tone: "text-zinc-300",
    numTone: "bg-zinc-700/40 text-zinc-300",
    owner: "Orchestration",
    ownerTone: "bg-teal-500/15 text-teal-300",
  },
  {
    label: "Resolved Contacts",
    desc: "Each role becomes one or more contacts (with email / phone) at runtime, never hard-coded.",
    icon: UsersThree,
    tone: "text-sky-300",
    numTone: "bg-sky-500/15 text-sky-300",
    owner: "Orchestration",
    ownerTone: "bg-teal-500/15 text-teal-300",
  },
  {
    label: "Delivery Channels",
    desc: "Per-rule channel set (in-app / email / SMS / Slack) fanned out × resolved contacts.",
    icon: Broadcast,
    tone: "text-violet-300",
    numTone: "bg-violet-500/15 text-violet-300",
    owner: "Orchestration",
    ownerTone: "bg-teal-500/15 text-teal-300",
  },
  {
    label: "Notification Log",
    desc: "One row per (recipient × channel) with delivery_status, expires_at, and escalation_step pointer.",
    icon: Clock,
    tone: "text-red-300",
    numTone: "bg-red-500/15 text-red-300",
    owner: "Orchestration",
    ownerTone: "bg-teal-500/15 text-teal-300",
  },
  {
    label: "Alert Center",
    desc: "Operator sees the alert with full dispatch trail, SLA countdown, and escalation level.",
    icon: Bell,
    tone: "text-amber-300",
    numTone: "bg-amber-500/15 text-amber-300",
    owner: "Cockpit",
    ownerTone: "bg-amber-500/15 text-amber-300",
  },
  {
    label: "Acknowledge / Resolve / Escalate",
    desc: "Operator drives lifecycle. ACK cancels pending escalations. Resolve / Dismiss close the alert.",
    icon: ListChecks,
    tone: "text-emerald-300",
    numTone: "bg-emerald-500/15 text-emerald-300",
    owner: "Cockpit",
    ownerTone: "bg-amber-500/15 text-amber-300",
  },
  {
    label: "Audit Trail",
    desc: "Every state change appends to the alert's audit_trail[] — compliance-ready, immutable timeline.",
    icon: ShieldCheck,
    tone: "text-emerald-300",
    numTone: "bg-emerald-500/15 text-emerald-300",
    owner: "Both",
    ownerTone: "bg-zinc-800 text-zinc-300",
  },
];

/* Render the inline pipeline strip arrows — kept for visual continuity if reused */
export function FlowArrow() {
  return <ArrowRight size={10} className="text-zinc-700" />;
}
